import { useCallback, useEffect, useRef, useState } from "react";

import type { CallContact } from "services/dialstack";

import { popCallerName, popCallerNumber } from "../incoming-call-pop.slice";
import type { InboundCallMatch } from "./use-inbound-call-pops.hook";

// How long an ended unknown call stays actionable in the widget. Long enough
// to finish a Quick Add that was started mid-call, or to attach a call the
// front desk only recognised after hanging up.
export const RECENT_CALLER_TTL_MS = 10 * 60_000;

export type RecentCaller = {
  callId: string;
  backendCallId: number;
  remoteNumber: string;
  fromName: string | null;
  contact: CallContact | null;
  clinicId: number | null;
  endedAt: number;
};

// Keeps the most recently ended unknown inbound call (one that had a pop, so
// the backend id is known) for RECENT_CALLER_TTL_MS or until dismissed. Lives
// in the widget so it outlives the SDK call and the live-call card.
export default function useRecentCaller(liveUnknown: InboundCallMatch[]) {
  const [recentCaller, setRecentCaller] = useState<RecentCaller | null>(null);
  const previous = useRef<InboundCallMatch[]>([]);

  useEffect(() => {
    const liveIds = new Set(liveUnknown.map((match) => match.callId));
    const ended = previous.current.find(
      (match) => !liveIds.has(match.callId) && match.backendCallId !== null
    );
    previous.current = liveUnknown;
    if (!ended?.pop || ended.backendCallId === null) return;
    setRecentCaller({
      callId: ended.callId,
      backendCallId: ended.backendCallId,
      remoteNumber: popCallerNumber(ended.pop),
      fromName: popCallerName(ended.pop),
      contact: ended.pop.contact,
      clinicId: ended.pop.clinicId,
      endedAt: Date.now()
    });
  }, [liveUnknown]);

  useEffect(() => {
    if (!recentCaller) return undefined;
    const remaining = recentCaller.endedAt + RECENT_CALLER_TTL_MS - Date.now();
    const timer = setTimeout(
      () => setRecentCaller(null),
      Math.max(remaining, 0)
    );
    return () => clearTimeout(timer);
  }, [recentCaller]);

  const dismissRecentCaller = useCallback(() => setRecentCaller(null), []);

  return { recentCaller, dismissRecentCaller };
}
